import { Controller } from "@hotwired/stimulus"

const NODE_WIDTH = 12
const NODE_PADDING = 10
const SVG_HEIGHT = 400
const MONO_FONT = "'SF Mono', 'JetBrains Mono', 'Menlo', monospace"

export default class extends Controller {
  static targets = ["container", "svg", "breadcrumb", "hideNonNsa"]
  static values = {
    nodes: Array,
    links: Array,
    colors: Object
  }

  connect() {
    this.rootNode = null
    this.hoveredNode = null
    this.hoveredLinkIdx = null
    // When checked, the baseline view is rooted at "NSA Eligible" so the (much
    // larger) "Not NSA Eligible" bucket doesn't compress the rest of the flow.
    this.hideNonNsa = this.hasHideNonNsaTarget ? this.hideNonNsaTarget.checked : false
    this.render()

    this.resizeObserver = new ResizeObserver(() => this.render())
    this.resizeObserver.observe(this.containerTarget)
  }

  disconnect() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
  }

  // --- Data filtering ---

  getDescendants(rootId) {
    const ids = new Set([rootId])
    let changed = true
    while (changed) {
      changed = false
      this.linksValue.forEach(l => {
        if (ids.has(l.source) && !ids.has(l.target)) {
          ids.add(l.target)
          changed = true
        }
      })
    }
    return ids
  }

  // Root the view at an explicit zoom target if set, otherwise at "NSA Eligible"
  // when the "Hide Non-NSA Claims" toggle is on, otherwise show everything.
  effectiveRoot() {
    if (this.rootNode) return this.rootNode
    if (this.hideNonNsa && this.nodesValue.some(n => n.id === "nsa_eligible")) {
      return "nsa_eligible"
    }
    return null
  }

  getFilteredData() {
    const allNodes = this.nodesValue
    const allLinks = this.linksValue
    const root = this.effectiveRoot()

    if (!root) {
      return { nodes: allNodes, links: allLinks }
    }

    const visibleIds = this.getDescendants(root)
    return {
      nodes: allNodes.filter(n => visibleIds.has(n.id)),
      links: allLinks.filter(l => visibleIds.has(l.source) && visibleIds.has(l.target))
    }
  }

  toggleNonNsa(event) {
    this.hideNonNsa = event.target.checked
    // Drop any manual zoom so the toggle returns to the baseline view.
    this.rootNode = null
    this.hoveredNode = null
    this.hoveredLinkIdx = null
    this.render()
  }

  // --- Breadcrumb ---

  getBreadcrumb(targetId) {
    if (!targetId) return []
    const path = [targetId]
    let current = targetId
    let safety = 20
    while (safety-- > 0) {
      const parentLink = this.linksValue.find(l => l.target === current)
      if (!parentLink) break
      path.unshift(parentLink.source)
      current = parentLink.source
    }
    return path
  }

  updateBreadcrumb() {
    const el = this.breadcrumbTarget
    if (!this.rootNode) {
      el.classList.add("hidden")
      el.innerHTML = ""
      return
    }

    el.classList.remove("hidden")
    const crumbs = this.getBreadcrumb(this.rootNode)
    const colors = this.colorsValue
    const nodeMap = Object.fromEntries(this.nodesValue.map(n => [n.id, n]))

    let html = ""
    crumbs.forEach((id, i) => {
      const node = nodeMap[id]
      const label = node ? node.label : id
      const color = colors[id] || "#9CA3AF"
      const isLast = i === crumbs.length - 1

      if (i > 0) {
        html += "<span class=\"text-support-dark-300 text-sm\">/</span>"
      }

      if (isLast) {
        html += "<span class=\"text-sm font-semibold\" style=\"color:" + color + "\">" + label + "</span>"
      } else {
        html += "<span data-action=\"click->sankey#zoomTo\" data-node-id=\"" + id + "\" class=\"text-sm text-link\">" + label + "</span>"
      }
    })

    html += "<span data-action=\"click->sankey#reset\" class=\"text-sm text-link ml-2\">Reset</span>"
    el.innerHTML = html
  }

  zoomTo(event) {
    const nodeId = event.currentTarget.dataset.nodeId
    const firstNodeId = this.nodesValue.length > 0 ? this.nodesValue[0].id : null
    if (nodeId === firstNodeId) {
      this.rootNode = null
    } else {
      this.rootNode = nodeId
    }
    this.hoveredNode = null
    this.hoveredLinkIdx = null
    this.render()
  }

  reset() {
    this.rootNode = null
    this.hoveredNode = null
    this.hoveredLinkIdx = null
    this.render()
  }

  // --- Layout Engine ---

  sankeyLayout(nodes, links, width, height) {
    const nodePad = NODE_PADDING
    const nodeWidth = NODE_WIDTH

    if (nodes.length === 0) return { nodes: [], links: [], nodeWidth }

    const nodeMap = {}
    nodes.forEach(n => {
      nodeMap[n.id] = { ...n, x: 0, y: 0, dy: 0, col: 0, sourceLinks: [], targetLinks: [] }
    })

    const linkObjs = links.filter(l => nodeMap[l.source] && nodeMap[l.target]).map(l => {
      const s = nodeMap[l.source]
      const t = nodeMap[l.target]
      const obj = { source: s, target: t, value: l.value, sy: 0, ty: 0, dy: 0 }
      s.sourceLinks.push(obj)
      t.targetLinks.push(obj)
      return obj
    })

    // BFS column assignment
    const visited = new Set()
    const queue = []
    Object.values(nodeMap).forEach(n => {
      if (n.targetLinks.length === 0) {
        n.col = 0
        visited.add(n.id)
        queue.push(n)
      }
    })
    while (queue.length) {
      const n = queue.shift()
      n.sourceLinks.forEach(l => {
        if (!visited.has(l.target.id)) {
          l.target.col = n.col + 1
          visited.add(l.target.id)
          queue.push(l.target)
        }
      })
    }

    const allNodes = Object.values(nodeMap)
    const maxCol = Math.max(...allNodes.map(n => n.col), 0)
    const colWidth = maxCol > 0 ? (width - nodeWidth) / maxCol : 0

    // Group nodes by column
    const columns = {}
    allNodes.forEach(n => {
      n.x = n.col * colWidth
      if (!columns[n.col]) columns[n.col] = []
      columns[n.col].push(n)
    })

    // Scale node heights proportionally
    const maxValue = Math.max(
      ...allNodes.map(n => Math.max(
        n.sourceLinks.reduce((s, l) => s + l.value, 0),
        n.targetLinks.reduce((s, l) => s + l.value, 0),
        n.value || 0
      )),
      1
    )
    const maxColSize = Math.max(...Object.values(columns).map(c => c.length), 1)
    const availH = height - (maxColSize - 1) * nodePad
    const scale = availH / maxValue

    Object.values(columns).forEach(colNodes => {
      let totalH = 0
      colNodes.forEach(n => {
        n.dy = Math.max(
          n.sourceLinks.reduce((s, l) => s + l.value, 0),
          n.targetLinks.reduce((s, l) => s + l.value, 0),
          n.value || 0
        ) * scale
        totalH += n.dy
      })
      const totalPad = (colNodes.length - 1) * nodePad
      let y = (height - totalH - totalPad) / 2
      colNodes.forEach(n => {
        n.y = y
        y += n.dy + nodePad
      })
    })

    // Position links on nodes
    allNodes.forEach(n => {
      let sy = n.y
      n.sourceLinks.sort((a, b) => a.target.y - b.target.y).forEach(l => {
        l.sy = sy
        l.dy = l.value * scale
        sy += l.dy
      })
      let ty = n.y
      n.targetLinks.sort((a, b) => a.source.y - b.source.y).forEach(l => {
        l.ty = ty
        ty += l.dy
      })
    })

    return { nodes: allNodes, links: linkObjs, nodeWidth }
  }

  sankeyPath(link) {
    const nw = NODE_WIDTH
    const x0 = link.source.x + nw
    const x1 = link.target.x
    const mid = (x0 + x1) / 2
    return "M" + x0 + "," + link.sy + " C" + mid + "," + link.sy + " " + mid + "," + link.ty + " " + x1 + "," + link.ty + " v" + link.dy + " C" + mid + "," + (link.ty + link.dy) + " " + mid + "," + (link.sy + link.dy) + " " + x0 + "," + (link.sy + link.dy) + " Z"
  }

  // --- Rendering ---

  render() {
    const container = this.containerTarget
    const svg = this.svgTarget
    const width = container.offsetWidth || 800

    const { nodes: filteredNodes, links: filteredLinks } = this.getFilteredData()
    const layout = this.sankeyLayout(filteredNodes, filteredLinks, width, SVG_HEIGHT)

    this.currentLayout = layout
    this.currentFilteredLinks = filteredLinks

    // Clear SVG
    svg.innerHTML = ""
    svg.setAttribute("width", width)
    svg.setAttribute("height", SVG_HEIGHT)
    svg.setAttribute("viewBox", "0 0 " + width + " " + SVG_HEIGHT)

    const colors = this.colorsValue

    // Draw links
    layout.links.forEach((l, i) => {
      const color = colors[l.target.id] || "#9CA3AF"
      const path = document.createElementNS("http://www.w3.org/2000/svg", "path")
      path.setAttribute("d", this.sankeyPath(l))
      path.setAttribute("fill", color)
      path.setAttribute("fill-opacity", "0.18")
      path.setAttribute("stroke", color)
      path.setAttribute("stroke-opacity", "0")
      path.setAttribute("stroke-width", "1")
      path.style.transition = "fill-opacity 0.2s, stroke-opacity 0.2s"
      path.style.cursor = "pointer"
      path.dataset.linkIdx = i
      path.dataset.sourceId = l.source.id
      path.dataset.targetId = l.target.id
      path.addEventListener("mouseenter", () => this.onLinkHover(i))
      path.addEventListener("mouseleave", () => this.onLinkLeave())
      svg.appendChild(path)
    })

    // Draw nodes
    const maxCol = layout.nodes.length > 0 ? Math.max(...layout.nodes.map(n => n.col)) : 0

    layout.nodes.forEach(n => {
      const color = colors[n.id] || "#9CA3AF"
      const hasChildren = filteredLinks.some(l => l.source === n.id)
      const isLastCol = n.col === maxCol && maxCol > 0
      const labelX = isLastCol ? n.x - 6 : n.x + NODE_WIDTH + 6
      const anchor = isLastCol ? "end" : "start"
      const labelY = n.y + Math.max(n.dy, 3) / 2

      const g = document.createElementNS("http://www.w3.org/2000/svg", "g")
      g.style.cursor = hasChildren ? "pointer" : "default"
      g.dataset.nodeId = n.id

      g.addEventListener("mouseenter", () => this.onNodeHover(n.id))
      g.addEventListener("mouseleave", () => this.onNodeLeave())
      if (hasChildren) {
        g.addEventListener("click", () => this.onNodeClick(n.id))
      }

      // Node rect
      const rect = document.createElementNS("http://www.w3.org/2000/svg", "rect")
      rect.setAttribute("x", n.x)
      rect.setAttribute("y", n.y)
      rect.setAttribute("width", NODE_WIDTH)
      rect.setAttribute("height", Math.max(n.dy, 3))
      rect.setAttribute("rx", 3)
      rect.setAttribute("fill", color)
      rect.setAttribute("opacity", "0.8")
      rect.style.transition = "opacity 0.2s"
      g.appendChild(rect)

      // Label
      const label = document.createElementNS("http://www.w3.org/2000/svg", "text")
      label.setAttribute("x", labelX)
      label.setAttribute("y", labelY - 4)
      label.setAttribute("text-anchor", anchor)
      label.setAttribute("fill", "#1A1D23")
      label.setAttribute("font-size", "11")
      label.setAttribute("font-weight", "600")
      label.textContent = n.label
      g.appendChild(label)

      // Value
      const val = document.createElementNS("http://www.w3.org/2000/svg", "text")
      val.setAttribute("x", labelX)
      val.setAttribute("y", labelY + 10)
      val.setAttribute("text-anchor", anchor)
      val.setAttribute("fill", color)
      val.setAttribute("font-size", "11")
      val.setAttribute("font-weight", "700")
      val.setAttribute("font-family", MONO_FONT)
      val.textContent = n.value.toLocaleString()
      g.appendChild(val)

      svg.appendChild(g)
    })

    this.updateBreadcrumb()
  }

  // --- Interaction ---

  onNodeClick(nodeId) {
    this.rootNode = nodeId
    this.hoveredNode = null
    this.hoveredLinkIdx = null
    this.render()
  }

  onNodeHover(nodeId) {
    this.hoveredNode = nodeId
    this.applyHoverState()
  }

  onNodeLeave() {
    this.hoveredNode = null
    this.applyHoverState()
  }

  onLinkHover(idx) {
    this.hoveredLinkIdx = idx
    this.applyHoverState()
  }

  onLinkLeave() {
    this.hoveredLinkIdx = null
    this.applyHoverState()
  }

  applyHoverState() {
    const svg = this.svgTarget
    const paths = svg.querySelectorAll("path")
    const groups = svg.querySelectorAll("g")

    paths.forEach((path, i) => {
      const sourceId = path.dataset.sourceId
      const targetId = path.dataset.targetId
      const isHov = this.hoveredLinkIdx === i ||
                    this.hoveredNode === sourceId ||
                    this.hoveredNode === targetId
      path.setAttribute("fill-opacity", isHov ? "0.45" : "0.18")
      path.setAttribute("stroke-opacity", isHov ? "0.6" : "0")
    })

    groups.forEach(g => {
      const nodeId = g.dataset.nodeId
      if (!nodeId) return
      const rect = g.querySelector("rect")
      if (!rect) return

      let isHov = this.hoveredNode === nodeId
      if (this.hoveredLinkIdx !== null) {
        const hovPath = paths[this.hoveredLinkIdx]
        if (hovPath) {
          isHov = isHov || hovPath.dataset.sourceId === nodeId || hovPath.dataset.targetId === nodeId
        }
      }
      rect.setAttribute("opacity", isHov ? "1" : "0.8")
    })
  }
}
